import { useFormikContext } from 'formik'
import React, { useEffect } from 'react'
import { compose, find, isNil, not, path, propEq, prop } from 'ramda'
import { useIntl } from 'react-intl'

import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import SelectorV2Formik from 'app/componentV4/selectors/SelectorV2Formik'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY, CANCEL } from 'app/constants/button'
import PhoneNumberInputV4, {
  validatePhoneNumber,
} from 'app/componentV4/Input/PhoneNumberInputV4'
import usePreferredChannel from 'app/componentV4/preferredChannel/usePreferredChannel'
import PreferredChannelSelector from 'app/componentV4/selectors/PreferredChannelSelector'
import { CONTACT_TYPE_CRM } from 'app/main/notification/socialMediaSelect/socialMediaSelectConfig'
import { CHANNEL_SMS, CHANNEL_EMAIL, CHANNEL_CALL } from 'app/constants/channel'
import {
  openWarningDialog,
  closeWarningDialog,
} from 'app/store/actions/warningDialog.actions'
import { useDispatch } from 'react-redux'
import PreferredLanguageSelector from 'app/componentV4/selectors/PreferredLanguageSelector'
import { twMerge } from 'tailwind-merge'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ErrorBadContact from 'app/main/subscribersV4/subscriberDetail/profiles/ErrorBadContact'
import { isEmptyOrNil } from 'libs/empty'
import { disableSpaceBar } from 'commons/keyDown'

const permissionOptions = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 0 },
]

const useStyles = makeStyles((theme) => ({
  formRoot: {
    '& .MuiFormControl-root': {
      '& .label-error': {
        marginTop: '4px',
      },
    },
  },
  inputFormat: {
    '& .MuiInputBase-root': {
      color: 'rgb(24 53 96 / var(--tw-text-opacity)) !important',
    },
    '& fieldset': {
      height: '53px !important',
      borderRadius: '.8rem !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 17px) scale(1)',
    },
  },
  labelInput: {
    '& span': {
      color: '#EF4444',
    },
  },
}))

const ContactInformationForm = ({
  subscriber,
  languages,
  onContactChange,
  onContactReset,
}) => {
  const formik = useFormikContext()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isButtonDisabled = !formik.dirty
  const isButtonLoading = formik.isValidating || formik.isSubmitting
  const dispatch = useDispatch()
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))

  const {
    state: { preferOptions },
    getPreferOptionsFromSubscriber,
    addOption,
    updateOption,
    removeOption,
  } = usePreferredChannel(subscriber)

  const handleFieldChange = (channel, value, isValidValue) => {
    formik.setFieldTouched('automatedCallPhoneNumber', true, false)
    formik.setFieldTouched('phoneNumber', true, false)
    formik.setFieldTouched('email', true, false)

    if (!isEmptyOrNil(value)) {
      if (isValidValue) {
        const isHasCrmContact = compose(
          not,
          isNil,
          find((option) => {
            return (
              option.contactType === CONTACT_TYPE_CRM &&
              option.channel === channel
            )
          }),
        )(preferOptions)

        isHasCrmContact
          ? updateOption({
              channel,
              contact: value,
            })
          : addOption({
              channel,
              contact: value,
            })
      }
    } else {
      removeOption({ channel })
      const preferredChannel = formik.values.preferredChannel
      if (
        propEq('channel', channel, preferredChannel) &&
        propEq('contactType', CONTACT_TYPE_CRM, preferredChannel)
      ) {
        formik.setFieldValue('preferredChannel', null)
        formik.setFieldTouched('preferredChannel', true, false)
      }
    }
  }

  const onAutomatedCallPhoneNumberChange = (number = '') => {
    const preferredChannel = formik.values.preferredChannel
    const isValidValue = validatePhoneNumber(number)
    if (
      prop('channel', preferredChannel) === CHANNEL_CALL &&
      prop('contactType', preferredChannel) === CONTACT_TYPE_CRM &&
      isValidValue
    ) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        preferredChannel: {
          ...preferredChannel,
          contact: number,
          isBadContact: false,
        },
        automatedCallPhoneNumber: number,
      }))
    } else {
      formik.setFieldValue('automatedCallPhoneNumber', number)
    }
    formik.setFieldTouched('automatedCallPhoneNumber', true, false)

    handleFieldChange(CHANNEL_CALL, number, isValidValue)
  }

  const onPhoneNumberChange = (number = '') => {
    const preferredChannel = formik.values.preferredChannel
    const isValidValue = validatePhoneNumber(number)
    if (
      prop('channel', preferredChannel) === CHANNEL_SMS &&
      prop('contactType', preferredChannel) === CONTACT_TYPE_CRM &&
      isValidValue
    ) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        preferredChannel: {
          ...preferredChannel,
          contact: number,
          isBadContact: false,
        },
        phoneNumber: number,
      }))
    } else {
      formik.setFieldValue('phoneNumber', number)
    }
    formik.setFieldTouched('phoneNumber', true, false)

    handleFieldChange(CHANNEL_SMS, number, isValidValue)
  }

  const onEmailChange = (event) => {
    const preferredChannel = formik.values.preferredChannel
    if (
      prop('channel', preferredChannel) === CHANNEL_EMAIL &&
      prop('contactType', preferredChannel) === CONTACT_TYPE_CRM
    ) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        preferredChannel: {
          ...preferredChannel,
          contact: event.target.value,
          isBadContact: false,
        },
        email: event.target.value,
      }))
    } else {
      formik.setFieldValue('email', event.target.value)
    }
    formik.setFieldTouched('email', true, false)
    handleFieldChange(CHANNEL_EMAIL, event.target.value, true)
  }

  const onCancelClick = () => {
    if (formik.dirty) {
      const payload = {
        title: formatMessage({ id: 'dialog.discard.title' }),
        description: formatMessage({ id: 'dialog.discard.body' }),
        submitText: formatMessage({
          id: 'draft.notification.create.popup.title',
        }),
        closeText: formatMessage({ id: 'dialog.discard.close' }),
        type: CANCEL,
        onSubmit: () => {
          formik.resetForm()
          onContactReset()
          dispatch(closeWarningDialog())
          getPreferOptionsFromSubscriber()
        },
        onClose: () => {
          dispatch(closeWarningDialog())
        },
      }
      dispatch(openWarningDialog(payload))
    }
  }

  const checkBadContact = (channel) => {
    const preferredCrmByChannel = find((prefer) => {
      return (
        prefer.channel === channel && prefer.contactType === CONTACT_TYPE_CRM
      )
    }, preferOptions)

    return isNil(preferredCrmByChannel)
      ? false
      : preferredCrmByChannel.isBadContact
  }

  useEffect(() => {
    getPreferOptionsFromSubscriber()
  }, [])

  useEffect(() => {
    if (formik.dirty) {
      onContactChange(formik.dirty)
    } else {
      onContactReset()
    }
  }, [formik.dirty])

  return (
    <div className="w-full">
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.notificationMethod',
          })}
          required
        >
          <PreferredChannelSelector
            id="preferredChannel"
            placeholder={formatMessage({
              id: 'crm.form.notificationMethod',
            })}
            value={formik.values.preferredChannel}
            className={twMerge('w-full', classes.inputFormat)}
            onChange={(value) => {
              formik.setFieldValue('preferredChannel', value)
              formik.setFieldTouched('preferredChannel', true, false)
            }}
            options={preferOptions}
            hideContact={subscriber.hideContacts === 1}
            error={
              formik.errors.preferredChannel && formik.touched.preferredChannel
            }
            helperText={formik.errors.preferredChannel}
          />
          <ErrorBadContact
            isBadContact={
              !formik.errors.preferredChannel &&
              path(['values', 'preferredChannel', 'isBadContact'], formik)
            }
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'preferred.language.label',
          })}
          required
        >
          <PreferredLanguageSelector
            placeholder={formatMessage({
              id: 'preferred.language.label',
            })}
            options={languages}
            className={twMerge('w-full', classes.inputFormat)}
            value={formik.values.preferredLanguage}
            onChange={(value) => {
              formik.setFieldValue('preferredLanguage', value)
              formik.setFieldTouched('preferredLanguage', true, false)
            }}
            error={
              formik.errors.preferredLanguage &&
              formik.touched.preferredLanguage
            }
            helperText={formik.errors.preferredLanguage}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.email',
          })}
        >
          <SimpleInput
            id="email"
            name="email"
            type="text"
            value={formik.values.email}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={onEmailChange}
            placeholder={formatMessage({
              id: 'crm.form.email',
            })}
            inputProps={{
              onKeyDown: (e) => {
                disableSpaceBar(e)
              },
            }}
            error={formik.errors.email && formik.touched.email}
            helperText={formik.errors.email}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'phone.label',
          })}
        >
          <PhoneNumberInputV4
            value={formik.values.phoneNumber}
            id="phoneNumber"
            name="phoneNumber"
            onChange={onPhoneNumberChange}
            error={formik.errors.phoneNumber && formik.touched.phoneNumber}
            helperText={formik.errors.phoneNumber}
          />
          <ErrorBadContact
            isBadContact={
              !formik.errors.phoneNumber && checkBadContact(CHANNEL_SMS)
            }
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'channel.automatedCall',
          })}
        >
          <PhoneNumberInputV4
            value={formik.values.automatedCallPhoneNumber}
            id="automatedCallPhoneNumber"
            name="automatedCallPhoneNumber"
            onChange={onAutomatedCallPhoneNumberChange}
            error={
              formik.errors.automatedCallPhoneNumber &&
              formik.touched.automatedCallPhoneNumber
            }
            helperText={formik.errors.automatedCallPhoneNumber}
            prefer={['US']}
          />
          <ErrorBadContact
            isBadContact={
              !formik.errors.automatedCallPhoneNumber &&
              checkBadContact(CHANNEL_CALL)
            }
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.contactable',
          })}
        >
          <SelectorV2Formik
            name="contactable"
            label={formatMessage({
              id: 'crm.form.contactable',
            })}
            options={permissionOptions}
          />
        </LabelInput>
      </div>
      <div className="flex justify-end space-x-5 mt-5">
        <ContainedButton
          color={CLOSE}
          onClick={onCancelClick}
          disabled={isButtonDisabled || isButtonLoading}
          type="button"
          className="w-96"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'action.cancel',
              })}
            </span>
          </div>
        </ContainedButton>
        <ContainedButton
          color={SECONDARY}
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          type="submit"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'subscriberCrm.editGroup.save.label',
              })}
            </span>
          </div>
        </ContainedButton>
      </div>
    </div>
  )
}

export default ContactInformationForm
